"use client"

import React, { useState, useRef } from 'react';
import { ScrollArea } from './scroll-area';
import { toast } from '@/components/ui';

interface CodeBlockProps {
  children: React.ReactNode;
  className?: string;
  language?: string;
  showLineNumbers?: boolean;
  theme?: 'default' | 'supernova';
}

/**
 * CodeBlock component for displaying code with syntax highlighting and copy functionality
 */
export function CodeBlock({ 
  children, 
  className = '', 
  language,
  showLineNumbers = false,
  theme = 'default'
}: CodeBlockProps) {
  const [copied, setCopied] = useState(false);
  const preRef = useRef<HTMLPreElement>(null);
  
  const handleCopy = () => {
    if (!preRef.current) return;
    
    // Get the text content from the code block
    const codeText = preRef.current.textContent || '';
    
    // Copy to clipboard using modern clipboard API with fallback
    try {
      navigator.clipboard.writeText(codeText).then(() => {
        setCopied(true);
        toast.success('Code copied to clipboard');
        setTimeout(() => setCopied(false), 2000);
      }).catch(err => {
        console.error('Failed to copy: ', err);
        fallbackCopyTextToClipboard(codeText);
      });
    } catch (err) {
      console.error('Failed to copy: ', err);
      fallbackCopyTextToClipboard(codeText);
    }
  };

  // Fallback method for older browsers
  const fallbackCopyTextToClipboard = (text: string) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    
    // Make the textarea out of viewport
    textArea.style.position = "fixed";
    textArea.style.left = "-999999px";
    textArea.style.top = "-999999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      if (successful) {
        setCopied(true);
        toast.success('Code copied to clipboard');
        setTimeout(() => setCopied(false), 2000);
      }
    } catch (err) {
      console.error('Fallback: Oops, unable to copy', err);
    }

    document.body.removeChild(textArea);
  };

  // Determine background styles based on theme
  const getBackgroundStyles = () => {
    switch (theme) {
      case 'supernova':
        return 'bg-gradient-to-br from-supernova-blue/5 via-supernova-purple/5 to-supernova-pink/5 dark:from-supernova-blue/10 dark:via-supernova-purple/10 dark:to-supernova-pink/10';
      default:
        return 'bg-muted/50 dark:bg-slate-950';
    }
  };

  // Determine button styles based on theme
  const getButtonStyles = () => {
    switch (theme) {
      case 'supernova':
        return 'bg-black/10 hover:bg-black/20 dark:bg-white/10 dark:hover:bg-white/20';
      default:
        return 'bg-muted hover:bg-muted/80 dark:bg-slate-800 dark:hover:bg-slate-700';
    }
  };

  return (
    <div className={`not-prose rounded-lg overflow-hidden border border-border ${className}`}>
      {language && (
        <div className="px-4 py-1.5 bg-muted/30 dark:bg-slate-900 border-b border-border flex items-center justify-between">
          <span className="text-xs text-muted-foreground font-mono">{language}</span>
        </div>
      )}
      <div className={`relative group ${getBackgroundStyles()}`}>
        <div className="absolute top-2 right-2 sm:opacity-0 opacity-70 group-hover:opacity-100 transition-opacity z-20">
          <button 
            className={`p-1.5 rounded ${getButtonStyles()} text-foreground transition-colors shadow-sm hover:shadow-md`}
            aria-label="Copy code"
            title="Copy code"
            onClick={handleCopy}
          >
            {copied ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
              </svg>
            )}
          </button>
        </div>
        <ScrollArea className="w-full max-h-[500px]">
          <pre ref={preRef} className="p-4">
            <code className={`text-sm font-mono whitespace-pre-wrap break-all sm:whitespace-pre sm:break-normal ${showLineNumbers ? 'line-numbers' : ''}`}>
              {children}
            </code>
          </pre>
        </ScrollArea>
      </div>
    </div>
  );
}

interface CodeBlockLineProps {
  children: React.ReactNode;
  className?: string;
  number?: number;
}

/**
 * CodeBlockLine component for displaying individual lines of code
 */
export function CodeBlockLine({ 
  children, 
  className = '',
  number
}: CodeBlockLineProps) {
  return (
    <div className={`${className} ${number ? 'flex' : ''}`}>
      {number && (
        <span className="text-muted-foreground opacity-50 mr-4 select-none text-right w-5">
          {number}
        </span>
      )}
      <span>{children}</span>
    </div>
  );
}